import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Button,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useMaterialReactTable } from "material-react-table";
import DataTable from "../../ui/data/DataTable";
import PageContainer from "../../ui/layout/PageContainer";
import BarChartComponent from "../../components/barChart/BarChartComponent";
import PieChartComponent from "../../components/barChart/PieChartComponent";
import "./snf.scss";

// CustomTabPanel component for tab content
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`snf-tabpanel-${index}`}
      aria-labelledby={`snf-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `snf-tab-${index}`,
    "aria-controls": `snf-tabpanel-${index}`,
  };
}

const SnfDashboard = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [abandonedCartStatuses, setAbandonedCartStatuses] = useState({});

  // ============ SAMPLE DATA ============

  // Summary Metrics Data
  const summaryMetrics = useMemo(
    () => [
      {
        label: "Total Employers (Purchased)",
        value: 247,
        trend: "+18%",
        trendUp: true,
      },
      {
        label: "Total Candidate Data Purchased",
        value: 3842,
        trend: "+24%",
        trendUp: true,
      },
      {
        label: "Total Searches",
        value: 15678,
        trend: "+31%",
        trendUp: true,
      },
      {
        label: "Total Abandoned Sessions",
        value: 89,
        trend: "-12%",
        trendUp: false,
      },
      {
        label: "Revenue (Monthly)",
        value: "₹18.4L",
        trend: "+22%",
        trendUp: true,
      },
      {
        label: "Revenue (Weekly)",
        value: "₹4.2L",
        trend: "+15%",
        trendUp: true,
      },
    ],
    []
  );

  // Revenue Chart Data
  const revenueChartData = useMemo(
    () => [
      { label: "Week 1", count: 95000 },
      { label: "Week 2", count: 108000 },
      { label: "Week 3", count: 112000 },
      { label: "Week 4", count: 125000 },
    ],
    []
  );

  // Category Distribution Data for Pie Chart
  const categoryDistributionData = useMemo(
    () => [
      { label: "Security Guard", value: 1247 },
      { label: "Warehouse Worker", value: 892 },
      { label: "Software Developer", value: 645 },
      { label: "Delivery Driver", value: 523 },
      { label: "Others", value: 535 },
    ],
    []
  );

  // Employers Data (Tab 1)
  const employersData = useMemo(
    () => [
      {
        employerId: "EMP001",
        employerName: "Tech Solutions Pvt Ltd",
        contactNumber: "9876543210",
        city: "Mumbai",
        totalSearches: 145,
        candidateDemand: 28,
        candidateDataProvided: 25,
        mostSearchedCategory: "Software Developer",
        urgencyLevel: "High",
      },
      {
        employerId: "EMP002",
        employerName: "Global Logistics Corp",
        contactNumber: "9823456789",
        city: "Delhi",
        totalSearches: 98,
        candidateDemand: 42,
        candidateDataProvided: 38,
        mostSearchedCategory: "Warehouse Worker",
        urgencyLevel: "Medium",
      },
      {
        employerId: "EMP003",
        employerName: "Prime Security Services",
        contactNumber: "9765432109",
        city: "Bangalore",
        totalSearches: 127,
        candidateDemand: 35,
        candidateDataProvided: 35,
        mostSearchedCategory: "Security Guard",
        urgencyLevel: "High",
      },
      {
        employerId: "EMP004",
        employerName: "Metro Hospitality Group",
        contactNumber: "9834567821",
        city: "Mumbai",
        totalSearches: 89,
        candidateDemand: 18,
        candidateDataProvided: 16,
        mostSearchedCategory: "Cook",
        urgencyLevel: "Low",
      },
      {
        employerId: "EMP005",
        employerName: "Rapid Courier Services",
        contactNumber: "9887654321",
        city: "Pune",
        totalSearches: 112,
        candidateDemand: 22,
        candidateDataProvided: 20,
        mostSearchedCategory: "Delivery Driver",
        urgencyLevel: "Medium",
      },
      {
        employerId: "EMP006",
        employerName: "Smart Retail Solutions",
        contactNumber: "9898989898",
        city: "Bangalore",
        totalSearches: 76,
        candidateDemand: 15,
        candidateDataProvided: 14,
        mostSearchedCategory: "Store Manager",
        urgencyLevel: "Low",
      },
      {
        employerId: "EMP007",
        employerName: "Urban Construction Ltd",
        contactNumber: "9812345678",
        city: "Delhi",
        totalSearches: 134,
        candidateDemand: 48,
        candidateDataProvided: 42,
        mostSearchedCategory: "Construction Worker",
        urgencyLevel: "High",
      },
      {
        employerId: "EMP008",
        employerName: "Elite Housekeeping Services",
        contactNumber: "9876512345",
        city: "Mumbai",
        totalSearches: 67,
        candidateDemand: 12,
        candidateDataProvided: 12,
        mostSearchedCategory: "Housekeeping Staff",
        urgencyLevel: "Low",
      },
      {
        employerId: "EMP009",
        employerName: "Fast Track Logistics",
        contactNumber: "9845678901",
        city: "Chennai",
        totalSearches: 103,
        candidateDemand: 31,
        candidateDataProvided: 28,
        mostSearchedCategory: "Logistics Coordinator",
        urgencyLevel: "Medium",
      },
      {
        employerId: "EMP010",
        employerName: "Digital Marketing Hub",
        contactNumber: "9923456780",
        city: "Bangalore",
        totalSearches: 58,
        candidateDemand: 9,
        candidateDataProvided: 8,
        mostSearchedCategory: "Content Writer",
        urgencyLevel: "Low",
      },
      {
        employerId: "EMP011",
        employerName: "Mega Manufacturing Co",
        contactNumber: "9878787878",
        city: "Pune",
        totalSearches: 156,
        candidateDemand: 65,
        candidateDataProvided: 58,
        mostSearchedCategory: "Factory Worker",
        urgencyLevel: "High",
      },
      {
        employerId: "EMP012",
        employerName: "Premium Healthcare Solutions",
        contactNumber: "9834512345",
        city: "Delhi",
        totalSearches: 91,
        candidateDemand: 19,
        candidateDataProvided: 18,
        mostSearchedCategory: "Nursing Staff",
        urgencyLevel: "Medium",
      },
    ],
    []
  );

  // Most Searches Data (Tab 2)
  const mostSearchesData = useMemo(
    () => [
      {
        rank: 1,
        city: "Mumbai",
        category: "Security Guard",
        searchCount: 2847,
        trend: "up",
      },
      {
        rank: 2,
        city: "Delhi",
        category: "Warehouse Worker",
        searchCount: 2456,
        trend: "up",
      },
      {
        rank: 3,
        city: "Bangalore",
        category: "Software Developer",
        searchCount: 2301,
        trend: "up",
      },
      {
        rank: 4,
        city: "Mumbai",
        category: "Delivery Driver",
        searchCount: 1987,
        trend: "down",
      },
      {
        rank: 5,
        city: "Chennai",
        category: "Cook",
        searchCount: 1823,
        trend: "up",
      },
      {
        rank: 6,
        city: "Pune",
        category: "Factory Worker",
        searchCount: 1756,
        trend: "up",
      },
      {
        rank: 7,
        city: "Delhi",
        category: "Construction Worker",
        searchCount: 1698,
        trend: "up",
      },
      {
        rank: 8,
        city: "Bangalore",
        category: "Sales Executive",
        searchCount: 1543,
        trend: "down",
      },
      {
        rank: 9,
        city: "Mumbai",
        category: "Housekeeping Staff",
        searchCount: 1421,
        trend: "up",
      },
      {
        rank: 10,
        city: "Hyderabad",
        category: "Customer Support",
        searchCount: 1389,
        trend: "up",
      },
      {
        rank: 11,
        city: "Chennai",
        category: "Electrician",
        searchCount: 1267,
        trend: "down",
      },
      {
        rank: 12,
        city: "Pune",
        category: "Data Entry Operator",
        searchCount: 1198,
        trend: "up",
      },
      {
        rank: 13,
        city: "Delhi",
        category: "Plumber",
        searchCount: 1145,
        trend: "up",
      },
      {
        rank: 14,
        city: "Bangalore",
        category: "Content Writer",
        searchCount: 1089,
        trend: "down",
      },
      {
        rank: 15,
        city: "Mumbai",
        category: "Receptionist",
        searchCount: 1023,
        trend: "up",
      },
      {
        rank: 16,
        city: "Kolkata",
        category: "Accountant",
        searchCount: 987,
        trend: "up",
      },
      {
        rank: 17,
        city: "Ahmedabad",
        category: "Warehouse Manager",
        searchCount: 945,
        trend: "down",
      },
      {
        rank: 18,
        city: "Chennai",
        category: "Mechanic",
        searchCount: 912,
        trend: "up",
      },
      {
        rank: 19,
        city: "Pune",
        category: "Store Manager",
        searchCount: 876,
        trend: "up",
      },
      {
        rank: 20,
        city: "Delhi",
        category: "Office Assistant",
        searchCount: 834,
        trend: "down",
      },
      {
        rank: 21,
        city: "Bangalore",
        category: "HR Executive",
        searchCount: 798,
        trend: "up",
      },
      {
        rank: 22,
        city: "Mumbai",
        category: "Telecaller",
        searchCount: 756,
        trend: "up",
      },
      {
        rank: 23,
        city: "Jaipur",
        category: "Marketing Executive",
        searchCount: 723,
        trend: "down",
      },
      {
        rank: 24,
        city: "Hyderabad",
        category: "Cleaner",
        searchCount: 689,
        trend: "up",
      },
      {
        rank: 25,
        city: "Chennai",
        category: "Teacher",
        searchCount: 654,
        trend: "up",
      },
    ],
    []
  );

  // Purchased Data (Tab 3)
  const purchasedData = useMemo(
    () => [
      {
        employerName: "Tech Solutions Pvt Ltd",
        employerId: "EMP001",
        contactNumber: "9876543210",
        candidateDemand: 5,
        dataProvided: 5,
        costOfPurchase: 2500,
        purchaseDate: "2026-02-23 14:35:22",
        city: "Mumbai",
        demandFulfilled: true,
      },
      {
        employerName: "Tech Solutions Pvt Ltd",
        employerId: "EMP001",
        contactNumber: "9876543210",
        candidateDemand: 3,
        dataProvided: 3,
        costOfPurchase: 1500,
        purchaseDate: "2026-02-17 10:20:15",
        city: "Mumbai",
        demandFulfilled: true,
      },
      {
        employerName: "Global Logistics Corp",
        employerId: "EMP002",
        contactNumber: "9823456789",
        candidateDemand: 10,
        dataProvided: 8,
        costOfPurchase: 4000,
        purchaseDate: "2026-02-22 16:45:30",
        city: "Delhi",
        demandFulfilled: false,
      },
      {
        employerName: "Prime Security Services",
        employerId: "EMP003",
        contactNumber: "9765432109",
        candidateDemand: 8,
        dataProvided: 8,
        costOfPurchase: 4000,
        purchaseDate: "2026-02-21 11:30:45",
        city: "Bangalore",
        demandFulfilled: true,
      },
      {
        employerName: "Metro Hospitality Group",
        employerId: "EMP004",
        contactNumber: "9834567821",
        candidateDemand: 4,
        dataProvided: 3,
        costOfPurchase: 1500,
        purchaseDate: "2026-02-20 09:15:20",
        city: "Mumbai",
        demandFulfilled: false,
      },
      {
        employerName: "Rapid Courier Services",
        employerId: "EMP005",
        contactNumber: "9887654321",
        candidateDemand: 6,
        dataProvided: 6,
        costOfPurchase: 3000,
        purchaseDate: "2026-02-19 14:22:10",
        city: "Pune",
        demandFulfilled: true,
      },
      {
        employerName: "Urban Construction Ltd",
        employerId: "EMP007",
        contactNumber: "9812345678",
        candidateDemand: 12,
        dataProvided: 10,
        costOfPurchase: 5000,
        purchaseDate: "2026-02-18 16:40:35",
        city: "Delhi",
        demandFulfilled: false,
      },
      {
        employerName: "Urban Construction Ltd",
        employerId: "EMP007",
        contactNumber: "9812345678",
        candidateDemand: 8,
        dataProvided: 8,
        costOfPurchase: 4000,
        purchaseDate: "2026-02-15 10:25:50",
        city: "Delhi",
        demandFulfilled: true,
      },
      {
        employerName: "Fast Track Logistics",
        employerId: "EMP009",
        contactNumber: "9845678901",
        candidateDemand: 7,
        dataProvided: 7,
        costOfPurchase: 3500,
        purchaseDate: "2026-02-17 13:18:25",
        city: "Chennai",
        demandFulfilled: true,
      },
      {
        employerName: "Mega Manufacturing Co",
        employerId: "EMP011",
        contactNumber: "9878787878",
        candidateDemand: 15,
        dataProvided: 12,
        costOfPurchase: 6000,
        purchaseDate: "2026-02-16 11:45:15",
        city: "Pune",
        demandFulfilled: false,
      },
      {
        employerName: "Mega Manufacturing Co",
        employerId: "EMP011",
        contactNumber: "9878787878",
        candidateDemand: 10,
        dataProvided: 10,
        costOfPurchase: 5000,
        purchaseDate: "2026-02-12 09:30:40",
        city: "Pune",
        demandFulfilled: true,
      },
      {
        employerName: "Premium Healthcare Solutions",
        employerId: "EMP012",
        contactNumber: "9834512345",
        candidateDemand: 5,
        dataProvided: 5,
        costOfPurchase: 2500,
        purchaseDate: "2026-02-14 15:20:30",
        city: "Delhi",
        demandFulfilled: true,
      },
      {
        employerName: "Smart Retail Solutions",
        employerId: "EMP006",
        contactNumber: "9898989898",
        candidateDemand: 3,
        dataProvided: 2,
        costOfPurchase: 1000,
        purchaseDate: "2026-02-13 12:10:15",
        city: "Bangalore",
        demandFulfilled: false,
      },
    ],
    []
  );

  // Abandoned Cart Data (Tab 4)
  const abandonedCartData = useMemo(
    () => [
      {
        employerName: "Tech Solutions Pvt Ltd",
        employerId: "EMP001",
        employerPhone: "9876543210",
        employerEmail: "contact@techsolutions.in",
        numberOfSessions: 3,
        dateTime: "2026-02-23 14:35:22",
        cartValue: 2800,
        searchesCity: "Mumbai",
        status: "Pending",
      },
      {
        employerName: "Metro Hospitality Group",
        employerId: "EMP004",
        employerPhone: "9834567821",
        employerEmail: "info@metrohospitality.com",
        numberOfSessions: 2,
        dateTime: "2026-02-23 11:20:45",
        cartValue: 1600,
        searchesCity: "Mumbai",
        status: "Contacted",
      },
      {
        employerName: "Smart Retail Solutions",
        employerId: "EMP006",
        employerPhone: "9898989898",
        employerEmail: "sales@smartretail.in",
        numberOfSessions: 1,
        dateTime: "2026-02-22 16:48:12",
        cartValue: 1400,
        searchesCity: "Bangalore",
        status: "Pending",
      },
      {
        employerName: "Elite Housekeeping Services",
        employerId: "EMP008",
        employerPhone: "9876512345",
        employerEmail: "contact@elitehousekeeping.com",
        numberOfSessions: 4,
        dateTime: "2026-02-22 10:15:33",
        cartValue: 1200,
        searchesCity: "Mumbai",
        status: "Follow-up Required",
      },
      {
        employerName: "Digital Marketing Hub",
        employerId: "EMP010",
        employerPhone: "9923456780",
        employerEmail: "hr@digitalmarketinghub.in",
        numberOfSessions: 1,
        dateTime: "2026-02-21 15:22:11",
        cartValue: 900,
        searchesCity: "Bangalore",
        status: "Contacted",
      },
      {
        employerName: "Fast Track Logistics",
        employerId: "EMP009",
        employerPhone: "9845678901",
        employerEmail: "hiring@fasttracklog.com",
        numberOfSessions: 2,
        dateTime: "2026-02-21 13:45:59",
        cartValue: 3100,
        searchesCity: "Chennai",
        status: "Pending",
      },
      {
        employerName: "Premium Healthcare Solutions",
        employerId: "EMP012",
        employerPhone: "9834512345",
        employerEmail: "recruitment@premiumhealth.in",
        numberOfSessions: 3,
        dateTime: "2026-02-20 17:30:24",
        cartValue: 1900,
        searchesCity: "Delhi",
        status: "Converted",
      },
      {
        employerName: "Urban Construction Ltd",
        employerId: "EMP007",
        employerPhone: "9812345678",
        employerEmail: "jobs@urbanconstruction.in",
        numberOfSessions: 5,
        dateTime: "2026-02-20 09:12:47",
        cartValue: 4800,
        searchesCity: "Delhi",
        status: "Follow-up Required",
      },
      {
        employerName: "Rapid Courier Services",
        employerId: "EMP005",
        employerPhone: "9887654321",
        employerEmail: "hr@rapidcourier.com",
        numberOfSessions: 2,
        dateTime: "2026-02-19 14:55:18",
        cartValue: 2000,
        searchesCity: "Pune",
        status: "Contacted",
      },
      {
        employerName: "Global Logistics Corp",
        employerId: "EMP002",
        employerPhone: "9823456789",
        employerEmail: "recruitment@globallogistics.in",
        numberOfSessions: 1,
        dateTime: "2026-02-19 11:40:33",
        cartValue: 3800,
        searchesCity: "Delhi",
        status: "Pending",
      },
      {
        employerName: "Prime Security Services",
        employerId: "EMP003",
        employerPhone: "9765432109",
        employerEmail: "admin@primesecurity.co.in",
        numberOfSessions: 3,
        dateTime: "2026-02-18 16:25:55",
        cartValue: 3500,
        searchesCity: "Bangalore",
        status: "Contacted",
      },
      {
        employerName: "Mega Manufacturing Co",
        employerId: "EMP011",
        employerPhone: "9878787878",
        employerEmail: "hiring@megamfg.in",
        numberOfSessions: 4,
        dateTime: "2026-02-18 12:08:19",
        cartValue: 5800,
        searchesCity: "Pune",
        status: "Follow-up Required",
      },
    ],
    []
  );

  // ============ TAB HANDLERS ============

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleStatusMenuOpen = (event, row) => {
    setStatusMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setSelectedRow(null);
  };

  const handleStatusChange = (newStatus) => {
    if (selectedRow) {
      setAbandonedCartStatuses(prev => ({
        ...prev,
        [selectedRow.original.employerId + selectedRow.original.dateTime]: newStatus
      }));
    }
    handleStatusMenuClose();
  };

  const getRowStatus = (row) => {
    const key = row.employerId + row.dateTime;
    return abandonedCartStatuses[key] || row.status;
  };

  // ============ EMPLOYERS TABLE (Tab 1) ============

  const employersColumns = useMemo(
    () => [
      {
        accessorKey: "employerId",
        header: "Employer ID",
        size: 120,
      },
      {
        accessorKey: "employerName",
        header: "Employer Name",
        size: 200,
      },
      {
        accessorKey: "contactNumber",
        header: "Contact Number",
        size: 140,
      },
      {
        accessorKey: "city",
        header: "City",
        size: 120,
        filterVariant: "select",
      },
      {
        accessorKey: "totalSearches",
        header: "Total Searches",
        size: 130,
      },
      {
        accessorKey: "candidateDemand",
        header: "Candidate Demand",
        size: 150,
      },
      {
        accessorKey: "candidateDataProvided",
        header: "Data Provided",
        size: 130,
      },
      {
        accessorKey: "mostSearchedCategory",
        header: "Most Searched Category",
        size: 200,
      },
      {
        accessorKey: "urgencyLevel",
        header: "Urgency",
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const color =
            value === "High" ? "error" : value === "Medium" ? "warning" : "success";
          return <Chip label={value} color={color} size="small" />;
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 100,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <IconButton
            color="primary"
            onClick={() =>
              navigate(`/employer/detail/${row.original.employerId}`)
            }
            title="View Details"
          >
            <VisibilityIcon />
          </IconButton>
        ),
      },
    ],
    [navigate]
  );

  const employersTable = useMaterialReactTable({
    columns: employersColumns,
    data: employersData,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enablePagination: true,
    enableSorting: true,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      density: "compact",
    },
  });

  // ============ MOST SEARCHES TABLE (Tab 2) ============

  const mostSearchesColumns = useMemo(
    () => [
      {
        accessorKey: "rank",
        header: "Rank",
        size: 80,
      },
      {
        accessorKey: "city",
        header: "City",
        size: 140,
        filterVariant: "select",
      },
      {
        accessorKey: "category",
        header: "Category/Service",
        size: 220,
      },
      {
        accessorKey: "searchCount",
        header: "Number of Searches",
        size: 160,
      },
      {
        accessorKey: "trend",
        header: "Trend",
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value === "up" ? (
            <TrendingUpIcon color="success" />
          ) : (
            <TrendingDownIcon color="error" />
          );
        },
      },
    ],
    []
  );

  const mostSearchesTable = useMaterialReactTable({
    columns: mostSearchesColumns,
    data: mostSearchesData,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enablePagination: true,
    enableSorting: true,
    initialState: {
      pagination: { pageSize: 15, pageIndex: 0 },
      density: "compact",
    },
  });

  // ============ PURCHASED DATA TABLE (Tab 3) ============

  const purchasedDataColumns = useMemo(
    () => [
      {
        accessorKey: "employerName",
        header: "Employer Name",
        size: 200,
      },
      {
        accessorKey: "employerId",
        header: "Employer ID",
        size: 120,
      },
      {
        accessorKey: "contactNumber",
        header: "Phone",
        size: 140,
      },
      {
        accessorKey: "candidateDemand",
        header: "Demand",
        size: 100,
      },
      {
        accessorKey: "dataProvided",
        header: "Provided",
        size: 100,
      },
      {
        accessorKey: "costOfPurchase",
        header: "Cost",
        size: 100,
        Cell: ({ cell }) => {
          return `₹${cell.getValue().toLocaleString()}`;
        },
      },
      {
        accessorKey: "purchaseDate",
        header: "Purchase Date",
        size: 180,
      },
      {
        accessorKey: "city",
        header: "City",
        size: 120,
        filterVariant: "select",
      },
      {
        accessorKey: "demandFulfilled",
        header: "Fulfillment",
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "success.main" }}>
              <TrendingUpIcon fontSize="small" />
              <Typography variant="body2">Fulfilled</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "error.main" }}>
              <TrendingDownIcon fontSize="small" />
              <Typography variant="body2">Partial</Typography>
            </Box>
          );
        },
      },
    ],
    []
  );

  const purchasedDataTable = useMaterialReactTable({
    columns: purchasedDataColumns,
    data: purchasedData,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enablePagination: true,
    enableSorting: true,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      density: "compact",
    },
  });

  // Purchased Data Summary Metrics
  const purchasedDataMetrics = useMemo(
    () => [
      {
        label: "Total Purchases",
        value: purchasedData.length,
      },
      {
        label: "Total Revenue",
        value: `₹${(purchasedData.reduce((sum, item) => sum + item.costOfPurchase, 0) / 1000).toFixed(1)}K`,
      },
      {
        label: "Avg. Purchase Value",
        value: `₹${Math.round(purchasedData.reduce((sum, item) => sum + item.costOfPurchase, 0) / purchasedData.length).toLocaleString()}`,
      },
      {
        label: "Fulfillment Rate",
        value: `${Math.round((purchasedData.filter(item => item.demandFulfilled).length / purchasedData.length) * 100)}%`,
      },
    ],
    [purchasedData]
  );

  // ============ ABANDONED CART TABLE (Tab 4) ============

  const abandonedCartColumns = useMemo(
    () => [
      {
        accessorKey: "employerName",
        header: "Employer Name",
        size: 200,
      },
      {
        accessorKey: "employerId",
        header: "Employer ID",
        size: 120,
      },
      {
        accessorKey: "employerPhone",
        header: "Phone",
        size: 140,
      },
      {
        accessorKey: "employerEmail",
        header: "Email",
        size: 220,
      },
      {
        accessorKey: "numberOfSessions",
        header: "Sessions",
        size: 100,
      },
      {
        accessorKey: "dateTime",
        header: "Date & Time",
        size: 180,
      },
      {
        accessorKey: "cartValue",
        header: "Cart Value",
        size: 130,
        Cell: ({ cell }) => {
          return `₹${cell.getValue().toLocaleString()}`;
        },
      },
      {
        accessorKey: "searchesCity",
        header: "City",
        size: 120,
        filterVariant: "select",
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
        filterVariant: "select",
        Cell: ({ cell, row }) => {
          const value = getRowStatus(row.original);
          const colorMap = {
            "Pending": "warning",
            "Contacted": "info",
            "Follow-up Required": "error",
            "Converted": "success",
          };
          return <Chip label={value} color={colorMap[value] || "default"} size="small" />;
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={(e) => handleStatusMenuOpen(e, row)}
          >
            Update
          </Button>
        ),
      },
    ],
    [abandonedCartStatuses, getRowStatus, handleStatusMenuOpen]
  );

  const abandonedCartTable = useMaterialReactTable({
    columns: abandonedCartColumns,
    data: abandonedCartData,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enablePagination: true,
    enableSorting: true,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      density: "compact",
    },
  });

  // ============ RENDER ============

  return (
    <PageContainer
      title="SNF Dashboard"
      subtitle="Smart Nearby Feature - Monitor candidate purchase data for employers and recruiters"
    >
      <div className="snf-dashboard">
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="SNF Dashboard Tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Dashboard / Metrics" {...a11yProps(0)} />
            <Tab label="Employers" {...a11yProps(1)} />
            <Tab label="Most Searches (City-wise)" {...a11yProps(2)} />
            <Tab label="Purchased Data" {...a11yProps(3)} />
            <Tab label="Abandoned Cart" {...a11yProps(4)} />
          </Tabs>
        </Box>

        {/* TAB 0: Dashboard / Metrics */}
        <CustomTabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {summaryMetrics.map((metric, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                <Card
                  className="snf-metric-card"
                  sx={{
                    boxShadow: "var(--jy-card-shadow)",
                    border: "1px solid var(--jy-border)",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {metric.label}
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ my: 1 }}>
                      {typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {metric.trendUp ? (
                        <TrendingUpIcon fontSize="small" color="success" />
                      ) : (
                        <TrendingDownIcon fontSize="small" color="error" />
                      )}
                      <Typography
                        variant="body2"
                        color={metric.trendUp ? "success.main" : "error.main"}
                      >
                        {metric.trend}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        vs last month
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                <CardContent>
                  <BarChartComponent
                    data={revenueChartData}
                    title="Weekly Revenue Trend"
                    aspect={2}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                <CardContent>
                  <PieChartComponent
                    data={categoryDistributionData}
                    title="Candidate Data by Category"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CustomTabPanel>

        {/* TAB 1: Employers Table */}
        <CustomTabPanel value={tabValue} index={1}>
          <DataTable table={employersTable} />
        </CustomTabPanel>

        {/* TAB 2: Most Searches (City-wise) */}
        <CustomTabPanel value={tabValue} index={2}>
          <DataTable table={mostSearchesTable} />
        </CustomTabPanel>

        {/* TAB 3: Purchased Data */}
        <CustomTabPanel value={tabValue} index={3}>
          {/* Summary Metrics for Purchased Data */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {purchasedDataMetrics.map((metric, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    boxShadow: "var(--jy-card-shadow)",
                    border: "1px solid var(--jy-border)",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {metric.label}
                    </Typography>
                    <Typography variant="h5" component="div" sx={{ mt: 1 }}>
                      {metric.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <DataTable table={purchasedDataTable} />
        </CustomTabPanel>

        {/* TAB 4: Abandoned Cart */}
        <CustomTabPanel value={tabValue} index={4}>
          <DataTable table={abandonedCartTable} />

          {/* Status Update Menu */}
          <Menu
            anchorEl={statusMenuAnchor}
            open={Boolean(statusMenuAnchor)}
            onClose={handleStatusMenuClose}
          >
            <MenuItem onClick={() => handleStatusChange("Pending")}>Pending</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Contacted")}>Contacted</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Follow-up Required")}>Follow-up Required</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Converted")}>Converted</MenuItem>
          </Menu>
        </CustomTabPanel>
      </div>
    </PageContainer>
  );
};

export default SnfDashboard;
